import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import CircleChart from '../CircleChart';
import { getDishAverages } from '../../utils/sustainabilityAverages';
import CO2eShowComponent from '../CO2eShowComponent';
import EthicsShow from '../EthicsShow';
import { updateMeal } from '../../api/api_meals';
import { fetchMealByIdThunk } from '../../store/slices/mealsReducer';
import { useTranslation } from '../../hooks/useTranslation';
import { menuwiseApiBaseURL } from '../../api/api_base';
import { useAppAlertDialog } from '../../hooks/useAppAlertDialog';
import MealPriceTotalComponent from '../MealPriceTotalComponent';

const MealSingleViewSummeryCard = ({
  item,
  onClick,
  className = '',
  darkMode = false
}) => {
  const dispatch = useDispatch();
  const averages = item ? getDishAverages(item) : { environment_average: 0, health_average: 0, social_average: 0 };
  const [editingImage, setEditingImage] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [imageUrlDraft, setImageUrlDraft] = useState("");
  const [imageFileDraft, setImageFileDraft] = useState(null);
  const [imageValidationError, setImageValidationError] = useState("");
  const [savingImage, setSavingImage] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);
  const { t } = useTranslation();
  const { showAlert, dialogNode } = useAppAlertDialog({
    closeLabel: t("Stäng"),
  });
  const maxImageSizeBytes = 2 * 1024 * 1024;
  const resolvedImageSrc = (() => {
    const image = item?.image;
    if (!image) return image;
    if (/^https?:\/\//i.test(image)) return image;

    if (image.startsWith('/')) {
      if (/^https?:\/\//i.test(menuwiseApiBaseURL)) {
        try {
          return `${new URL(menuwiseApiBaseURL).origin}${image}`;
        } catch (_e) {
          return image;
        }
      }
      return image;
    }

    return image;
  })();

  if (!item) {
    return null;
  }

  const openEditImage = () => {
    setEditingImage(true);
    setImageUrlDraft(item?.image || "");
    setImageFileDraft(null);
    setImageValidationError("");
  };

  const cancelEditImage = () => {
    setEditingImage(false);
    setShowUploadModal(false);
    setImageUrlDraft("");
    setImageFileDraft(null);
    setImageValidationError("");
  };

  const saveImageUrl = async () => {
    if (!imageUrlDraft || savingImage) return;
    try {
      setSavingImage(true);
      await updateMeal(item.id, { image: imageUrlDraft });

      if (item?.id) {
        dispatch(fetchMealByIdThunk(item.id));
      }

      cancelEditImage();
    } catch (error) {
      console.error('Failed to update meal image URL:', error);
    } finally {
      setSavingImage(false);
    }
  };

  const saveImage = async () => {
    if (!imageFileDraft || savingImage) return;

    try {
      setSavingImage(true);
      await updateMeal(item.id, { image_file: imageFileDraft });

      if (item?.id) {
        dispatch(fetchMealByIdThunk(item.id));
      }

      cancelEditImage();
    } catch (error) {
      console.error('Failed to update meal image:', error);
    } finally {
      setSavingImage(false);
    }
  };

  const handleStartEditName = () => {
    if (!item?.name) return;
    setEditingName(true);
    setEditingNameValue(item.name);
  };

  const handleCancelEditName = () => {
    setEditingName(false);
    setEditingNameValue("");
  };

  const handleSaveName = async () => {
    if (!item?.id || savingName || !editingNameValue.trim()) return;
    
    const trimmedName = editingNameValue.trim();
    if (trimmedName === item?.name) {
      handleCancelEditName();
      return;
    }

    setSavingName(true);
    try {
      await updateMeal(item.id, { name: trimmedName });
      dispatch(fetchMealByIdThunk(item.id));
      handleCancelEditName();
    } catch (error) {
      console.error('Failed to update meal name:', error);
      showAlert(
        t("Fel"),
        t("Kunde inte uppdatera måltidsnamnet. Försök igen.") || "Kunde inte uppdatera måltidsnamnet. Försök igen."
      );
    } finally {
      setSavingName(false);
    }
  };

  const handleKeyDownName = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditName();
    }
  };

  const renderImage = () => (
    <div className="flex flex-col items-center relative">
      <div className="w-28 h-28 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm relative group">
        {resolvedImageSrc && resolvedImageSrc !== "" ? (
          <img
            src={resolvedImageSrc}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              console.log("Image failed to load:", item.image);
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
            onLoad={(e) => {
              console.log("Image loaded successfully:", item.image);
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'none';
              }
            }}
          />
        ) : null}
       <div className="border-2 border-secondary-green rounded-xl flex items-center justify-center">
       <img
          src="/assets/icons/svg/default-image-placeholder.svg"
          alt={t("Ingen bild")}
          className={`w-12 h-12 text-gray-400 ${item.image && item.image !== "" ? 'hidden' : 'flex'} items-center justify-center`}
        />
       </div>

        {/* Modern Image Edit Overlay */}
        {editingImage && (
          <div className="absolute inset-0 z-10 bg-white/95 backdrop-blur-sm flex flex-col justify-center p-2">
            <input
              type="url"
              value={imageUrlDraft}
              onChange={(e) => setImageUrlDraft(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-secondary-green rounded-md focus:outline-none focus:ring-1 focus:ring-secondary-green focus:border-transparent bg-white"
              placeholder={t("Bild URL (valfritt)")}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveImageUrl();
                } else if (e.key === 'Escape') {
                  cancelEditImage();
                }
              }}
              onBlur={() => {
                if (imageUrlDraft !== (item.image || "")) {
                  saveImageUrl();
                } else {
                  cancelEditImage();
                }
              }}
              autoFocus
            />
            {savingImage && (
              <div className="mt-1 flex justify-center">
                <div className="w-4 h-4 border-2 border-secondary-green border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        )}

        {/* Modern Edit Button - Always visible */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditImage(); }}
          className="absolute bottom-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 hover:bg-white border border-gray-200 shadow-sm transition-all duration-200 opacity-60 hover:opacity-100"
          title={t("Redigera bild")}
        >
          <img
            src="/assets/icons/Redigera.png"
            alt={t("Redigera")}
            className="w-3 h-3 text-gray-600"
          />
        </button>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowUploadModal(true); }}
          className="absolute bottom-1 left-1 z-10 inline-flex items-center justify-center px-2 h-6 rounded-full bg-white/90 hover:bg-white border border-gray-200 shadow-sm transition-all duration-200 opacity-70 hover:opacity-100 text-[10px]"
          title={t('Ladda upp bild')}
        >
          {t('Ladda upp')}
        </button>
      </div>

      {/* Meal type tag under image */}
      {item.meal_type && (
        <div className="flex justify-center gap-1 mt-2">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
          >
            {item.meal_type === 'frukost' ? t('Frukost') :
             item.meal_type === 'lunch' ? t('Lunch') :
             item.meal_type === 'middag' ? t('Middag') :
             item.meal_type === 'mellanmål' ? t('Mellanmål') :
             item.meal_type === 'småmål' ? t('Småmål') :
             item.meal_type === 'kvällsmål' ? t('Kvällsmål') :
             item.meal_type === 'desser' ? t('Desser') :
             item.meal_type === 'övrigt' ? t('Övrigt') :
             item.meal_type}
          </span>
        </div>
      )}
    </div>
  );

  const renderUploadModal = () => {
    if (!showUploadModal) return null;
    return (
      <div className="fixed inset-0 z-[60] flex items-start justify-center pt-96 overflow-y-auto">
        <div className="absolute inset-0 bg-black/40" onClick={cancelEditImage} />
        <div className="relative bg-white rounded-xl p-4 w-full max-w-md mx-4">
          <h4 className="text-sm font-semibold mb-3">{t('Ladda upp bild')}</h4>
          <p className="text-xs text-gray-500 mb-2">{t('Endast JPEG/PNG, max 2 MB.')}</p>
          <input
            id="meal-image-upload-input"
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) {
                setImageFileDraft(null);
                setImageValidationError("");
                return;
              }
              if (!['image/png', 'image/jpeg'].includes(file.type)) {
                setImageValidationError(t('Endast PNG och JPEG är tillåtet'));
                setImageFileDraft(null);
                return;
              }
              if (file.size > maxImageSizeBytes) {
                setImageValidationError(t('Max bildstorlek är 2 MB'));
                setImageFileDraft(null);
                return;
              }
              setImageValidationError("");
              setImageFileDraft(file);
            }}
            className="hidden"
          />
          <div className="flex items-center gap-3">
            <label
              htmlFor="meal-image-upload-input"
              className="inline-flex items-center justify-center px-3 py-1.5 text-sm border rounded cursor-pointer hover:bg-gray-50"
            >
              {t('Välj bild')}
            </label>
            <p className="text-xs text-gray-600">
              {imageFileDraft ? imageFileDraft.name : t('Ingen fil vald')}
            </p>
          </div>
          {!!imageValidationError && <p className="mt-2 text-xs text-red-600">{imageValidationError}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="px-3 py-1.5 text-sm border rounded" onClick={cancelEditImage}>
              {t('Avbryt')}
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm bg-secondary-green text-white rounded disabled:opacity-50"
              onClick={saveImage}
              disabled={!imageFileDraft || savingImage}
            >
              {savingImage ? t('Laddar upp...') : t('Ladda upp')}
            </button>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            {t('Tips: Du kan också lägga in en bildlänk via pennikonen.')}
          </p>
        </div>
      </div>
    );
  };

  const renderInfo = () => (
    <div>
      {editingName ? (
        <div className="mb-2">
          <input
            type="text"
            value={editingNameValue}
            onChange={(e) => setEditingNameValue(e.target.value)}
            onKeyDown={handleKeyDownName}
            onBlur={handleSaveName}
            autoFocus
            disabled={savingName}
            className="text-lg font-bold text-gray-900 px-2 py-1 border-2 border-secondary-green rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green w-full max-w-md"
            placeholder={t("Måltidsnamn") || "Måltidsnamn"}
          />
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-2 group/name">
          <h3 
            className="text-lg font-bold text-gray-900 cursor-pointer hover:text-secondary-green transition-colors"
            onClick={handleStartEditName}
            title={t("Klicka för att redigera") || "Klicka för att redigera"}
          >
            {item.name}
          </h3>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleStartEditName();
            }}
            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded"
            title={t("Redigera namn") || "Redigera namn"}
          >
            <svg className="w-4 h-4 text-gray-500 hover:text-secondary-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </div>
      )}
      {item.patient_profile && (
        <span className="text-sm text-gray-600">{t('Patient')}: {item.patient_profile.name}</span>
      )}
      <div className={`flex items-center space-x-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {/* Ethics information */}
        <div className="my-2">
          <EthicsShow
            isAnimal={item.is_animal}
            isAnimalKilled={item.is_animal_killed}
            isVegan={item.is_vegan}
            isVegetarian={item.is_vegetarian}
            ethicsUnknownConfirmed={item.ethics_unknown_confirmed}
            size="medium"
            showLabels={true}
            className={darkMode ? 'text-white' : ''}
          />
        </div>
      </div>

      {/* Size and Unit */}
      {item.size && item.unit_abbr && (
        <div className="flex items-center space-x-2 mt-1">
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('Storlek')}: {item.size} {item.unit_abbr}
          </span>
        </div>
      )}
    </div>
  );

  const renderCharts = () => (
    <div className="flex items-center space-x-4 justify-center">
      <CircleChart value={averages.environment_average || 0} size={64} strokeWidth={8} label={t("Miljö")} />
      <CircleChart value={averages.health_average || 0} size={64} strokeWidth={8} label={t("Hälsa")} />
      <CircleChart value={averages.social_average || 0} size={64} strokeWidth={8} label={t("Rättvisa")} />
    </div>
  );

  // Default variant (same as hero but without hover effects)
  return (
    <>
    <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-2xl shadow-sm border p-8 ${className}`}>
      <div className="flex flex-col tablet:flex-row laptop:flex-row desktop:flex-row tablet:items-center laptop:items-center desktop:items-center tablet:justify-between laptop:justify-between desktop:justify-between">
        <div className="flex items-start space-x-6">
          {renderImage()}
          {renderInfo()}
        </div>
        <div className="mt-6 tablet:mt-0 laptop:mt-0 desktop:mt-0 flex items-center justify-center flex-1 min-w-0">
          {renderCharts()}
        </div>
        <div className="flex flex-col items-center justify-center flex-shrink-0 min-w-[280px]">

        {item.CO2e_menuwise && (
          <CO2eShowComponent 
            co2ePerKg={item.CO2e_menuwise} 
            size={item.size} 
            unit_id={item.unit_id || 3} // Use meal's unit_id, default to 3 (grams) if not set
            cookedCo2ePerKg={item.cooked_co2e_per_kg}
            cookingWeightLoss={0} // Meals don't have weight loss
            fullWidth={true}
          />
        )}
        <MealPriceTotalComponent totalCost={item?.purchase_price_total} />
      </div>
        </div>
    </div>
    {renderUploadModal()}
    {dialogNode}
    </>
  );
};

export default MealSingleViewSummeryCard;

